import { getImage, IGatsbyImageData, ImageDataLike } from 'gatsby-plugin-image';
import { CONTENTSTACK, ENVIRONMENT, NETLIFY_IMAGE_SITE_URL } from '../../../constants';
import { ContentStackRawAsset, ContentStackRawAssetWithColor, CsAssetSize } from './content-item';
import { ImageSource } from './types';

/**
 * IMAGE FORMAT STANDARDS FOR CONTENTSTACK RESPONSIVE IMAGES
 * =========================================================
 *
 * FORMAT ORDERING (browser selection priority):
 * 1. AVIF - Best compression (~50% smaller than WebP)
 * 2. WebP - Good compression (~25-30% smaller than JPEG)
 * 3. JPEG - Universal fallback (all browsers)
 *
 * CONTENTSTACK FORMAT PARAMETERS:
 * - format=avif (modern browsers: Chrome 85+, Firefox 93+, Safari 16.1+)
 * - format=webp (wide support: Chrome 23+, Firefox 65+, Safari 14+)
 * - format=jpg (universal support - NOTE: Contentstack uses 'jpg' not 'jpeg')
 *
 * QUALITY PARAMETER:
 * - Range: 1-100 (percentage)
 * - Only works with lossy formats: JPEG, Progressive JPEG, AVIF, WebP (Lossy)
 */

// The biggest we'll allow an image to go, this 2160 size is used by other Gatsby Image implementations
const MAX_WIDTH = 2160;
// Default quality level for all images
const QUALITY = 75;

// Standard responsive breakpoints (commonly used widths)
const RESPONSIVE_BREAKPOINTS = [180, 360, 540, 720, 1080, 1440, 1920] as const;

// Contentstack format configuration with proper type mapping
/*
* This is now the source of truth for what gets generated.
*/
const CONTENTSTACK_FORMATS = {
    avif: {
        format: 'avif' as const,
        type: 'image/avif' as const,
        supportsQuality: true,
    },
    webp: {
        format: 'webp' as const,
        type: 'image/webp' as const,
        supportsQuality: true,
    },
    jpg: {
        format: 'jpg' as const, // Contentstack uses 'jpg', not 'jpeg'
        type: 'image/jpeg' as const,
        supportsQuality: true,
    },
} as const;

export type ImageFormat = keyof typeof CONTENTSTACK_FORMATS;

const IMAGE_FORMATS = Object.keys(CONTENTSTACK_FORMATS) as ImageFormat[];

const FRANCHISE_ADMIN_IMAGE_ORIGIN = 'https://franchiseadmin.servpro.com/FranchiseContent/';
const CONTENTSTACK_IMAGE_ORIGIN = 'https://images.contentstack.io';

export const isFranchiseAdminImageUrl = (url: string): boolean => url.startsWith(FRANCHISE_ADMIN_IMAGE_ORIGIN);

/**
 * Dual-source asset URLs: probe branchless raw first, then ?branch= when env branch is set.
 * Main-stack assets keep branchless transforms; branch-only uploads preserve branch on transforms.
 */
export type ResolvedAssetOrigin = {
    baseUrl: string
    requiresBranch: boolean
    useRawOnly: boolean
};
export const normalizeContentstackAssetUrl = (url: string): string => {
    if (!url.startsWith(CONTENTSTACK_IMAGE_ORIGIN)) {
        return url;
    }
    try {
        const parsed = new URL(url);
        parsed.searchParams.delete('branch');
        return parsed.toString();
    } catch {
        return url;
    }
};

const DIRECT_URL_HEAD_TIMEOUT_MS = 5000;
const assetOriginCache = new Map<string, ResolvedAssetOrigin>();

type ContentStackAssetWithOrigin = ContentStackRawAsset & {
    resolvedRequiresBranch?: boolean
    resolvedUseRawOnly?: boolean
};

export const appendContentstackBranchParam = (url: string, branch: string): string => {
    if (!url.startsWith(CONTENTSTACK_IMAGE_ORIGIN) || !branch) {
        return url;
    }
    try {
        const parsed = new URL(normalizeContentstackAssetUrl(url));
        parsed.searchParams.set('branch', branch);
        return parsed.toString();
    } catch {
        return url;
    }
};

export const clearContentstackAssetOriginCacheForTests = (): void => {
    assetOriginCache.clear();
};

/** @deprecated use clearContentstackAssetOriginCacheForTests */
export const clearContentstackDirectAssetUrlCacheForTests = clearContentstackAssetOriginCacheForTests;

const headOk = async (url: string): Promise<boolean> => {
    try {
        const res = await fetch(url, {
            method: 'HEAD',
            signal: AbortSignal.timeout(DIRECT_URL_HEAD_TIMEOUT_MS),
        });
        return res.ok;
    } catch {
        return false;
    }
};

export const appendContentstackImageParamsToBase = (
    baseUrl: string,
    params: Record<string, string | number>,
): string => {
    try {
        const parsed = new URL(baseUrl);
        Object.entries(params).forEach(([key, value]) => {
            parsed.searchParams.set(key, String(value));
        });
        return parsed.toString();
    } catch {
        return baseUrl;
    }
};

export const appendContentstackImageParams = (
    url: string,
    params: Record<string, string | number>,
): string => {
    const normalized = normalizeContentstackAssetUrl(url);
    return appendContentstackImageParamsToBase(normalized, params);
};

export const buildContentstackTransformUrl = (
    baseUrl: string,
    requiresBranch: boolean,
    params: Record<string, string | number>,
): string => {
    if (requiresBranch) {
        return appendContentstackImageParamsToBase(baseUrl, params);
    }
    return appendContentstackImageParams(baseUrl, params);
};

const branchlessFallbackOrigin = (base: string): ResolvedAssetOrigin => ({
    baseUrl: base,
    requiresBranch: false,
    useRawOnly: false,
});

const resolveBranchTransformStrategy = async (
    origin: ResolvedAssetOrigin,
): Promise<Pick<ResolvedAssetOrigin, 'useRawOnly'>> => {
    if (!origin.requiresBranch) {
        return { useRawOnly: false };
    }
    const sampleTransform = appendContentstackImageParamsToBase(origin.baseUrl, {
        width: 720,
        format: 'webp',
        quality: QUALITY,
    });
    if (await headOk(sampleTransform)) {
        return { useRawOnly: false };
    }
    return { useRawOnly: true };
};

/** Probe branchless raw then branch-qualified raw; cache winner per build. */
export const resolveContentstackAssetOrigin = async (url: string): Promise<ResolvedAssetOrigin> => {
    if (!url.startsWith(CONTENTSTACK_IMAGE_ORIGIN)) {
        return { baseUrl: url, requiresBranch: false, useRawOnly: false };
    }

    const cacheKey = normalizeContentstackAssetUrl(url);
    const cached = assetOriginCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    const branch = CONTENTSTACK.SERVER_SIDE.CONTENTSTACK_BRANCH;
    const branchlessUrl = cacheKey;
    let resolved = branchlessFallbackOrigin(branchlessUrl);

    if (await headOk(branchlessUrl)) {
        resolved = { baseUrl: branchlessUrl, requiresBranch: false, useRawOnly: false };
    } else if (branch) {
        const branchUrl = appendContentstackBranchParam(branchlessUrl, branch);
        if (await headOk(branchUrl)) {
            resolved = { baseUrl: branchUrl, requiresBranch: true, useRawOnly: false };
        }
    }

    if (resolved.requiresBranch) {
        const transformStrategy = await resolveBranchTransformStrategy(resolved);
        resolved = { ...resolved, ...transformStrategy };
    }

    assetOriginCache.set(cacheKey, resolved);
    return resolved;
};

/** Probe branchless then branch-qualified URL; cache winner per build. */
export const resolveContentstackDirectAssetUrl = async (url: string): Promise<string> => {
    const origin = await resolveContentstackAssetOrigin(url);
    return origin.baseUrl;
};

const isDirectContentstackAsset = (contentType: string): boolean => (
    contentType === 'image/svg+xml' || contentType === 'image/gif'
);

const withResolvedAssetOrigin = async <T extends ContentStackRawAsset>(
    asset: T,
): Promise<T & ContentStackAssetWithOrigin> => {
    if (!asset.url?.startsWith(CONTENTSTACK_IMAGE_ORIGIN)) {
        return asset;
    }
    const origin = await resolveContentstackAssetOrigin(asset.url);
    return {
        ...asset,
        url: origin.baseUrl,
        resolvedRequiresBranch: origin.requiresBranch,
        resolvedUseRawOnly: origin.useRawOnly,
    };
};

/** Default width for in-HTML FranchiseContent image rewrites (e.g. unique content). */
const DEFAULT_HTML_IMAGE_WIDTH = 720;

/**
 * Returns Netlify Image CDN URL for a FranchiseContent image, or the original URL if not on Netlify / not FranchiseContent.
 * Site base matches gatsby-node `GATSBY_BASE_URL`: `URL` first, then `DEPLOY_PRIME_URL` (Netlify-injected).
 * If both are unset, uses a site-relative `/.netlify/images` path so the browser resolves the current origin.
 */
export const getFranchiseAdminImageCdnUrl = (url: string, width: number = DEFAULT_HTML_IMAGE_WIDTH, fm: string = 'jpg'): string => {
    if (!isFranchiseAdminImageUrl(url)) return url;
    if (!ENVIRONMENT.IS_RUNNING_ON_NETLIFY) return url;
    try {
        const pathname = new URL(url).pathname.toLowerCase();
        if (pathname.endsWith('.svg')) return url;
    } catch {
        // ignore
    }
    const siteBase = NETLIFY_IMAGE_SITE_URL.replace(/\/$/, '');
    const imagePath = `/.netlify/images?url=${encodeURIComponent(url)}&w=${width}&q=${QUALITY}&fm=${fm}`;
    return siteBase ? `${siteBase}${imagePath}` : imagePath;
};

/**
 * Rewrites img src attributes in HTML that point to /FranchiseContent/ to use Netlify Image CDN.
 * Use for unique content / rawContent that may contain franchiseadmin image URLs.
 */
export const rewriteFranchiseContentImageUrlsInHtml = (
    html: string,
    defaultWidth: number = DEFAULT_HTML_IMAGE_WIDTH,
): string => {
    if (!html || typeof html !== 'string') return html;
    return html.replace(
        /\ssrc=["']([^"']*\/FranchiseContent\/[^"']*)["']/gi,
        (_, url: string) => ` src="${getFranchiseAdminImageCdnUrl(url, defaultWidth)}"`,
    );
};

/**
 * Generates image source configuration for Contentstack API or Netlify Image CDN (franchiseadmin URLs)
 * @param asset - The raw asset from Contentstack or a synthetic asset with url/content_type
 * @param width - Target width for the image
 * @param params - Additional query parameters (format, quality, etc.)
 * @returns ImageSource configuration object
 */
const generateImageSource = (
    asset: ContentStackAssetWithOrigin,
    width: number,
    params: Record<string, string|number> = {},
): ImageSource => {
    if (isFranchiseAdminImageUrl(asset.url)) {
        if (asset.content_type === 'image/svg+xml') {
            return { finalSrc: asset.url, contentStackSrc: asset.url, width };
        }
        const fm = (params.format as string) || 'jpg';
        const finalSrc = getFranchiseAdminImageCdnUrl(asset.url, width, fm);
        return { finalSrc, contentStackSrc: asset.url, width };
    }

    const isDirectAsset = isDirectContentstackAsset(asset.content_type);
    const requiresBranch = asset.resolvedRequiresBranch === true;
    const useRawOnly = asset.resolvedUseRawOnly === true;
    const baseUrl = isDirectAsset || requiresBranch ? asset.url : normalizeContentstackAssetUrl(asset.url);
    let contentStackSrc = baseUrl;

    if (!isDirectAsset && !useRawOnly) {
        contentStackSrc = buildContentstackTransformUrl(baseUrl, requiresBranch, {
            ...params,
            width,
            quality: QUALITY,
        });
    }

    let asUrl: URL;
    try {
        asUrl = new URL(contentStackSrc);
    } catch {
        return { finalSrc: asset.url, contentStackSrc: asset.url, width };
    }
    const finalSrcWithoutHost = `${asUrl.pathname}${asUrl.search}`;

    if (ENVIRONMENT.IS_RUNNING_ON_NETLIFY) {
        return {
            finalSrc: `/dcs-images${finalSrcWithoutHost}`,
            contentStackSrc,
            width,
        };
    }

    return {
        finalSrc: contentStackSrc,
        contentStackSrc,
        width,
    };
};

/**
 * Generates responsive image sources for multiple formats using standard breakpoints
 * @param asset - The raw asset from Contentstack
 * @param targetWidth - The target display width
 * @param formats - Array of image formats (avif, webp, jpg)
 * @returns Object mapping format to array of ImageSource objects for different sizes
 */
const pathnameLooksLikePng = (url: string): boolean => {
    if (!url) {
        return false;
    }
    // Relative paths (e.g. /dcs-images/.../logo.png?width=) — new URL(url) throws without a base
    if (/\.png(\?|#|$)/i.test(url)) {
        return true;
    }
    try {
        return /\.png$/i.test(new URL(url).pathname);
    } catch {
        return /\.png/i.test(url);
    }
};

/** Resolver payloads sometimes omit content_type; URL/filename still end in .png — needed for WebP fallback + letterbox fix. */
const assetLooksLikePng = (asset: ContentStackRawAssetWithColor): boolean => {
    if (asset.content_type === 'image/png') {
        return true;
    }
    if (asset.filename && /\.png$/i.test(asset.filename)) {
        return true;
    }
    if (asset.url && pathnameLooksLikePng(asset.url)) {
        return true;
    }
    // Title-only or odd CMS shapes: any `.png` in the string (encoded URLs, missing strict pathname)
    if (asset.url && asset.url.includes('.png')) {
        return true;
    }
    if (asset.filename && asset.filename.includes('.png')) {
        return true;
    }
    return false;
};

/**
 * On Netlify, /.netlify/images can re-encode remote franchiseadmin PNGs and flatten transparency.
 * Local dev (no NETLIFY) uses raw URLs — deploy should match. Skip AVIF/WebP sources and CDN for those.
 */
const franchisePngNetlifyDirectUrls = (
    asset: ContentStackRawAsset,
    targetWidth: number,
): Record<ImageFormat, ImageSource[]> | null => {
    if (!ENVIRONMENT.IS_RUNNING_ON_NETLIFY || !isFranchiseAdminImageUrl(asset.url)) {
        return null;
    }
    if (asset.content_type !== 'image/png' && !pathnameLooksLikePng(asset.url)) {
        return null;
    }
    const maxAllowedWidth = Math.min(targetWidth, MAX_WIDTH);
    let relevantBreakpoints = RESPONSIVE_BREAKPOINTS.filter(
        breakpoint => breakpoint <= maxAllowedWidth,
    );
    if (relevantBreakpoints.length === 0) {
        relevantBreakpoints = [RESPONSIVE_BREAKPOINTS[0]];
    }
    const shared: ImageSource[] = relevantBreakpoints.map(width => ({
        finalSrc: asset.url,
        contentStackSrc: asset.url,
        width,
    }));
    const empty: ImageSource[] = [];
    return { avif: empty, webp: empty, jpg: shared };
};

export const generateResponsiveImageSources = (
    asset: ContentStackRawAsset,
    targetWidth: number,
    formats: ImageFormat[],
): Record<ImageFormat, ImageSource[]> => {
    const direct = franchisePngNetlifyDirectUrls(asset, targetWidth);
    if (direct) {
        return direct;
    }
    const result: Record<ImageFormat, ImageSource[]> = {} as Record<ImageFormat, ImageSource[]>;
    formats.forEach((format) => {
        const formatConfig = CONTENTSTACK_FORMATS[format];

        // Filter breakpoints that don't exceed target width or MAX_WIDTH
        const maxAllowedWidth = Math.min(targetWidth, MAX_WIDTH);
        let relevantBreakpoints = RESPONSIVE_BREAKPOINTS.filter(
            breakpoint => breakpoint <= maxAllowedWidth,
        );

        // If no breakpoints match (image too small), use the smallest breakpoint
        // This ensures small images still generate at least one source
        if (relevantBreakpoints.length === 0) {
            relevantBreakpoints = [RESPONSIVE_BREAKPOINTS[0]];
        }

        result[format] = relevantBreakpoints.map((width) => {
            const params: Record<string, string|number> = {
                width,
                format: formatConfig.format,
            };
            if (formatConfig.supportsQuality) {
                params.quality = QUALITY;
            }
            return generateImageSource(asset, width, params);
        });
    });
    return result;
};

type WidthAndHeight = { width: number, height: number };
type WidthHeightSize = { width: number, height: number, size: CsAssetSize };

const CONTAINER_MAX = 1920;
const MOBILE_BREAKPOINT = 767;

const clamp = (p: number): number => Math.round(CONTAINER_MAX * (p / 100));

/**
 * Derives the target width in pixels from a CsAssetSize enum
 * This allows width to be automatically calculated when only size is provided
 */
export const getWidthFromSize = (size: CsAssetSize): number => {
    switch (size) {
        case CsAssetSize.QTR_100VW:
        case CsAssetSize.QTR_MAX_WIDTH:
            return 360; // 25% of 1440px container
        case CsAssetSize.THIRD_100VW:
        case CsAssetSize.THIRD_MAX_WIDTH:
            return 480; // 33.33% of 1440px container
        case CsAssetSize.HALF_100VW:
        case CsAssetSize.HALF_MAX_WIDTH:
            return 720; // 50% of 1440px container
        case CsAssetSize.FULL_100VW:
        case CsAssetSize.FULL_MAX_WIDTH:
        default:
            return 1440; // Full container width
    }
};

export const getSizesProp = (
    size: CsAssetSize,
): string => {
    switch (size) {
        case CsAssetSize.FULL_100VW:
            return '100vw';
        case CsAssetSize.HALF_100VW: {
            const base = '50vw';
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, ${base}`;
        }
        case CsAssetSize.THIRD_100VW: {
            const base = '33.33vw';
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, ${base}`;
        }
        case CsAssetSize.QTR_100VW: {
            const base = '25vw';
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, ${base}`;
        }
        case CsAssetSize.FULL_MAX_WIDTH: {
            return `(max-width: ${CONTAINER_MAX}px) 100vw, ${CONTAINER_MAX}px`;
        }
        case CsAssetSize.HALF_MAX_WIDTH: {
            const base = 50;
            const pxClamp = clamp(base);
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, (max-width: ${CONTAINER_MAX}px) ${base}vw, ${pxClamp}px`;
        }
        case CsAssetSize.THIRD_MAX_WIDTH: {
            const base = 33.33;
            const pxClamp = clamp(base);
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, (max-width: ${CONTAINER_MAX}px) ${base}vw, ${pxClamp}px`;
        }
        case CsAssetSize.QTR_MAX_WIDTH: {
            const base = 25;
            const pxClamp = clamp(base);
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, (max-width: ${CONTAINER_MAX}px) ${base}vw, ${pxClamp}px`;
        }
        default:
            return '100vw';
    }
};

const getSizeAfterTransformations = (
    original: WidthAndHeight,
    requested: Partial<WidthAndHeight>,
): WidthAndHeight => {
    let { width, height } = original;

    if (requested?.width) {
        const scales = [];

        if (requested?.width) {
            scales.push(requested.width / width);
        }

        if (requested?.height) {
            scales.push(requested.height / height);
        }

        const scale = Math.min(...scales);

        width = Math.round(scale * width);
        height = Math.round(scale * height);

        return { width, height };
    }

    return { width, height };
};

/**
 * Generates properly ordered sources for <picture> element
 * Format order: AVIF first (best compression), then WebP, then JPEG fallback
 *
 * For assets marked transparent (empty color from sync), the <img> fallback must not be JPEG —
 * JPEG has no alpha and CDNs composite transparency to an opaque matte (often white).
 */
/**
 * Picks <img> fallback src/srcset rows. Uses WebP for alpha-friendly assets when available.
 * Note: `[]` is truthy in JS — must check `.length`. Franchise PNG bypass only populates `jpg` with PNG URLs.
 */
const pickFallbackImageSources = (
    formatSources: Record<ImageFormat, ImageSource[]>,
    useAlphaCapableFallback: boolean,
): ImageSource[] => {
    const wantWebp = useAlphaCapableFallback;
    const primaryKey: ImageFormat = wantWebp ? 'webp' : 'jpg';
    const primary = formatSources[primaryKey];
    if (primary && primary.length > 0) {
        return primary;
    }
    if (wantWebp && formatSources.jpg && formatSources.jpg.length > 0) {
        return formatSources.jpg;
    }
    return formatSources.jpg && formatSources.jpg.length > 0 ? formatSources.jpg : [];
};

const generateResolvedImage = (
    imageAsset: ContentStackRawAssetWithColor,
    formatSources: Record<ImageFormat, ImageSource[]>,
    size: WidthHeightSize,
): ResolvedImageData => {
    // WebP supports alpha; JPEG does not — transparent PNGs get a matte in format=jpg from the CDN.
    // Use WebP for <img> when sync marked the asset transparent, or when the asset is a PNG (by type or URL/filename).
    const useAlphaCapableFallback = imageAsset.color === '' || assetLooksLikePng(imageAsset);
    const fallbackArr = pickFallbackImageSources(formatSources, useAlphaCapableFallback);
    const fallbackImg = fallbackArr.length > 0 ? fallbackArr[fallbackArr.length - 1] : { finalSrc: '', width: 0 };
    const sizeProp = getSizesProp(size.size);
    // AVIF + WebP <source> rows; <img> uses jpg (opaque) or webp (transparent asset — JPEG cannot hold alpha)
    const sources = IMAGE_FORMATS.filter(format => format !== 'jpg')
        .map(format => ({
            type: CONTENTSTACK_FORMATS[format].type,
            srcSet: (formatSources[format] || []).map(x => `${x.finalSrc} ${x.width}w`).join(',\n'),
            sizes: sizeProp,
        }))
        .filter(row => row.srcSet.length > 0);
    return {
        childImageSharp: imageAsset.content_type === 'image/svg+xml' ? null : {
            images: {
                sources,
                fallback: {
                    src: fallbackImg.finalSrc,
                    srcSet: fallbackArr.map(x => `${x.finalSrc} ${x.width}w`).join(',\n'),
                    sizes: sizeProp,
                },
            },
            layout: 'constrained',
            // PNG / alpha-friendly assets: never use average hex here — with object-fit:contain it fills
            // letterboxing around the graphic (e.g. navy from a star logo reads as a solid blue box).
            backgroundColor: useAlphaCapableFallback ? '' : (imageAsset.color || ''),
            width: size.width,
            height: size.height,
        },
        publicURL: fallbackImg.finalSrc,
    };
};

export const processImagePublicUrl = async (
    imageAsset: ContentStackRawAsset,
    resizeParams: Partial<WidthHeightSize>,
): Promise<ResolvedImageData|null> => {
    const resolvedAsset = await withResolvedAssetOrigin(imageAsset);
    const { dimension } = resolvedAsset;

    const finalSize: WidthHeightSize = dimension
        ? { ...getSizeAfterTransformations(dimension, resizeParams), size: CsAssetSize.FULL_100VW }
        : {
            width: resizeParams.width ?? 1440,
            height: resizeParams.height ?? resizeParams.width ?? 1440,
            size: CsAssetSize.FULL_100VW,
        };

    if (isDirectContentstackAsset(resolvedAsset.content_type)) {
        const directSource = generateImageSource(
            resolvedAsset,
            finalSize.width,
            {},
        );

        const directSources = IMAGE_FORMATS.reduce((acc, format) => {
            acc[format] = [directSource];
            return acc;
        }, {} as Record<ImageFormat, ImageSource[]>);
        return generateResolvedImage(resolvedAsset, directSources, {
            ...finalSize,
            size: CsAssetSize.FULL_100VW,
        });
    }

    // Generate all sources in one call
    const sources = generateResponsiveImageSources(resolvedAsset, finalSize.width, IMAGE_FORMATS);
    return generateResolvedImage(resolvedAsset, sources, finalSize);
};

export type ResolvedImageData = {
    childImageSharp: IGatsbyImageData|null
    publicURL: string
};

/**
 * Main function for processing responsive images with multiple formats and densities
 * Generates AVIF, WebP, and JPEG sources at different density levels
 */
const processImageResolution = async (
    imageAsset: ContentStackRawAssetWithColor,
    resizeParams: Partial<WidthHeightSize>,
): Promise<ResolvedImageData|null> => {
    const resolvedAsset = await withResolvedAssetOrigin(imageAsset);
    const { dimension } = resolvedAsset;

    const effectiveParams = { ...resizeParams };
    if (!effectiveParams.width && effectiveParams.size) {
        effectiveParams.width = getWidthFromSize(effectiveParams.size);
    }

    const finalSize: WidthHeightSize = dimension
        ? { ...getSizeAfterTransformations(dimension, effectiveParams), size: effectiveParams.size ?? CsAssetSize.FULL_100VW }
        : {
            width: effectiveParams.width ?? getWidthFromSize(effectiveParams.size ?? CsAssetSize.FULL_100VW),
            height: effectiveParams.height ?? effectiveParams.width ?? getWidthFromSize(effectiveParams.size ?? CsAssetSize.FULL_100VW),
            size: effectiveParams.size ?? CsAssetSize.FULL_100VW,
        };

    if (isDirectContentstackAsset(resolvedAsset.content_type)) {
        const directSource = generateImageSource(
            resolvedAsset,
            finalSize.width,
            {},
        );
        const directSources = IMAGE_FORMATS.reduce((acc, format) => {
            acc[format] = [directSource];
            return acc;
        }, {} as Record<ImageFormat, ImageSource[]>);
        return generateResolvedImage(resolvedAsset, directSources, {
            ...finalSize,
            size: effectiveParams.size || CsAssetSize.FULL_100VW,
        });
    }

    // Generate all sources in one call
    const sources = generateResponsiveImageSources(resolvedAsset, finalSize.width, IMAGE_FORMATS);
    return generateResolvedImage(resolvedAsset, sources, {
        ...finalSize,
        size: effectiveParams.size || CsAssetSize.FULL_100VW,
    });
};

const inferContentTypeFromUrl = (url: string): string => {
    try {
        const pathname = new URL(url).pathname.toLowerCase();
        if (pathname.endsWith('.svg')) return 'image/svg+xml';
        if (pathname.endsWith('.gif')) return 'image/gif';
        if (pathname.endsWith('.webp')) return 'image/webp';
        if (pathname.endsWith('.jpg') || pathname.endsWith('.jpeg')) return 'image/jpeg';
        if (pathname.endsWith('.png')) return 'image/png';
    } catch {
        // ignore
    }
    return 'image/png';
};

export type ResolveImagePreloadSrcOptions = {
    defaultWidth?: number;
};

const inferContentTypeForPreload = (img: Partial<ContentStackImage>): string => {
    const imagePath = img.filename || img.url || '';
    if (imagePath.includes('.svg')) {
        return 'image/svg+xml';
    }
    if (imagePath.includes('.gif')) {
        return 'image/gif';
    }
    if (img.url) {
        return inferContentTypeFromUrl(img.url);
    }
    return 'image/png';
};

export type ResponsiveContentstackSources = {
    src: string
    srcSet: string
};

export type BuildResponsiveContentstackSourcesOptions = {
    /** Primary `src` width when the layout size is unknown. Defaults to 720. */
    defaultWidth?: number
};

/**
 * Builds a sized Contentstack fallback `src` + responsive `srcSet` for the Image primitive
 * when `contentStackAsset` / GatsbyImage data is unavailable.
 */
export const buildResponsiveContentstackSources = (
    url: string,
    options?: BuildResponsiveContentstackSourcesOptions,
): ResponsiveContentstackSources => {
    if (!url.startsWith(CONTENTSTACK_IMAGE_ORIGIN)) {
        return { src: url, srcSet: '' };
    }

    const defaultWidth = options?.defaultWidth ?? DEFAULT_HTML_IMAGE_WIDTH;
    const normalized = normalizeContentstackAssetUrl(url);

    const toFinalSrc = (width: number): string => {
        try {
            const urlObj = new URL(normalized);
            urlObj.searchParams.set('width', String(width));
            urlObj.searchParams.set('quality', String(QUALITY));
            const absolute = urlObj.toString();
            if (ENVIRONMENT.IS_RUNNING_ON_NETLIFY) {
                const parsed = new URL(absolute);
                return `/dcs-images${parsed.pathname}${parsed.search}`;
            }
            return absolute;
        } catch {
            return url;
        }
    };

    const src = toFinalSrc(defaultWidth);
    const srcSet = RESPONSIVE_BREAKPOINTS
        .map(width => `${toFinalSrc(width)} ${width}w`)
        .join(', ');

    return { src, srcSet };
};

/** Sync preload URL — matches generateImageSource / GatsbyImage fallback used by Image. */
export const resolveImagePreloadSrc = (
    img: Partial<ContentStackImage> | null | undefined,
    options?: ResolveImagePreloadSrcOptions,
): string | null => {
    if (!img?.url) {
        return null;
    }

    const contentStackAsset = img.contentStackAsset as {
        childImageSharp?: unknown;
        publicURL?: string;
    } | null | undefined;

    if (contentStackAsset && typeof contentStackAsset === 'object') {
        if ('childImageSharp' in contentStackAsset && contentStackAsset.childImageSharp) {
            const gatsbyData = getImage(contentStackAsset.childImageSharp as ImageDataLike);
            if (gatsbyData?.images?.fallback?.src) {
                return gatsbyData.images.fallback.src;
            }
        }
        if (contentStackAsset.publicURL) {
            return contentStackAsset.publicURL;
        }
    }

    const width = options?.defaultWidth ?? DEFAULT_HTML_IMAGE_WIDTH;
    const syntheticAsset: ContentStackRawAsset = {
        url: img.url,
        content_type: inferContentTypeForPreload(img),
        filename: img.filename || img.url.split('/').pop() || '',
        updated_at: '',
    };
    const source = generateImageSource(syntheticAsset, width, { format: 'jpg' });
    return source.finalSrc || null;
};

export type ImagePreloadLinkProps = {
    imageSrcSet: string
    imageSizes: string
    type: 'image/avif'
};

export type BuildImagePreloadLinkPropsOptions = {
    /** Matches the `sizes` attribute on the hero `<Image>`. */
    sizes?: string
    /** Used when `sizes` is omitted. Defaults to full viewport. */
    size?: CsAssetSize
};

const PRELOAD_TARGET_WIDTH = RESPONSIVE_BREAKPOINTS[RESPONSIVE_BREAKPOINTS.length - 1];
const DCS_IMAGES_PREFIX = '/dcs-images';

const isContentstackDeliveryUrl = (url: string): boolean => (
    url.startsWith(CONTENTSTACK_IMAGE_ORIGIN) || url.startsWith(DCS_IMAGES_PREFIX)
);

const gatsbyImageDataFrom = (node: unknown): IGatsbyImageData | undefined => {
    if (!node || typeof node !== 'object') {
        return undefined;
    }
    return getImage(node as ImageDataLike) ?? undefined;
};

const childImageSharpOf = (value: unknown): unknown => {
    if (!value || typeof value !== 'object' || !('childImageSharp' in value)) {
        return undefined;
    }
    return (value as { childImageSharp?: unknown }).childImageSharp;
};

const resolvePreloadGatsbyData = (
    img: Partial<ContentStackImage>,
): IGatsbyImageData | undefined => (
    gatsbyImageDataFrom(childImageSharpOf(img.contentStackAsset))
    ?? gatsbyImageDataFrom(childImageSharpOf(img))
    ?? gatsbyImageDataFrom(img)
);

const toContentstackOriginUrl = (url: string): string => {
    if (!url.startsWith(DCS_IMAGES_PREFIX)) {
        return url;
    }
    return `${CONTENTSTACK_IMAGE_ORIGIN}${url.slice(DCS_IMAGES_PREFIX.length)}`;
};

const shouldProxyPreloadDelivery = (imgUrl: string, srcSet: string): boolean => {
    const delivery = isContentstackDeliveryUrl(imgUrl)
        || srcSet.includes(CONTENTSTACK_IMAGE_ORIGIN)
        || srcSet.includes(`${DCS_IMAGES_PREFIX}/`);
    if (!delivery) {
        return false;
    }
    return ENVIRONMENT.IS_RUNNING_ON_NETLIFY
        || imgUrl.startsWith(DCS_IMAGES_PREFIX)
        || srcSet.includes(`${DCS_IMAGES_PREFIX}/`);
};

const proxyContentstackDeliveryUrl = (raw: string): string => {
    const trimmed = raw.trim();
    if (trimmed.startsWith(DCS_IMAGES_PREFIX)) {
        return trimmed;
    }
    if (!trimmed.startsWith(CONTENTSTACK_IMAGE_ORIGIN)) {
        return trimmed;
    }
    try {
        const parsed = new URL(trimmed);
        return `${DCS_IMAGES_PREFIX}${parsed.pathname}${parsed.search}`;
    } catch {
        return trimmed;
    }
};

const rewritePreloadSrcSet = (imgUrl: string, srcSet: string): string => {
    if (!shouldProxyPreloadDelivery(imgUrl, srcSet)) {
        return srcSet;
    }
    return srcSet
        .split(',')
        .map((part) => {
            const piece = part.trim();
            const widthDescriptor = piece.match(/^(.*\S)\s+(\d+w)$/);
            if (!widthDescriptor) {
                return proxyContentstackDeliveryUrl(piece);
            }
            return `${proxyContentstackDeliveryUrl(widthDescriptor[1])} ${widthDescriptor[2]}`;
        })
        .join(',\n');
};

/**
 * AVIF `srcset` + `sizes` for `<link rel="preload" as="image">`.
 * Prefers the resolved Gatsby AVIF source; otherwise builds the same Contentstack breakpoints as `<Image>`.
 * Contentstack CDN candidates are rewritten to `/dcs-images` when that proxy already serves the asset.
 */
export const buildImagePreloadLinkProps = (
    img: Partial<ContentStackImage> | null | undefined,
    options?: BuildImagePreloadLinkPropsOptions,
): ImagePreloadLinkProps | null => {
    if (!img?.url) {
        return null;
    }

    const contentType = inferContentTypeForPreload(img);
    if (contentType === 'image/svg+xml' || contentType === 'image/gif') {
        return null;
    }

    const fallbackSizes = options?.sizes || getSizesProp(options?.size ?? CsAssetSize.FULL_100VW);

    const avif = resolvePreloadGatsbyData(img)?.images?.sources?.find(
        source => source.type === CONTENTSTACK_FORMATS.avif.type,
    );
    if (avif?.srcSet) {
        return {
            imageSrcSet: rewritePreloadSrcSet(img.url, avif.srcSet),
            imageSizes: options?.sizes || avif.sizes || fallbackSizes,
            type: 'image/avif',
        };
    }

    if (!isContentstackDeliveryUrl(img.url)) {
        return null;
    }

    const syntheticAsset: ContentStackRawAsset = {
        url: toContentstackOriginUrl(img.url),
        content_type: contentType,
        filename: img.filename || img.url.split('/').pop() || '',
        updated_at: '',
    };
    const avifSources = generateResponsiveImageSources(
        syntheticAsset,
        PRELOAD_TARGET_WIDTH,
        ['avif'],
    ).avif ?? [];
    if (avifSources.length === 0) {
        return null;
    }

    return {
        imageSrcSet: rewritePreloadSrcSet(
            img.url,
            avifSources.map(source => `${source.finalSrc} ${source.width}w`).join(',\n'),
        ),
        imageSizes: fallbackSizes,
        type: 'image/avif',
    };
};

export type BuildFranchiseAdminImageDataOptions = {
    targetWidth?: number
    targetHeight?: number
    size?: CsAssetSize
    /** Optional override for the sizes attribute (same as ContentStack). When set, applied to every source and fallback. */
    sizes?: string
};

/**
 * Builds ResolvedImageData for a franchiseadmin.servpro.com/FranchiseContent image URL.
 * Same shape/attributes as ContentStack: picture with source (avif), source (webp), img fallback,
 * each with sizes and srcset. Uses getSizesProp(size) unless options.sizes is provided.
 */
export const buildFranchiseAdminImageData = (
    url: string,
    options?: BuildFranchiseAdminImageDataOptions,
): ResolvedImageData => {
    const targetWidth = options?.targetWidth ?? 1440;
    const targetHeight = options?.targetHeight ?? targetWidth;
    const size = options?.size ?? CsAssetSize.FULL_100VW;
    const sizesOverride = options?.sizes;
    const contentType = inferContentTypeFromUrl(url);
    const syntheticAsset: ContentStackRawAssetWithColor = {
        url,
        content_type: contentType,
        filename: getFranchiseAdminImageCdnUrl(url),
        updated_at: '',
        color: '',
    };
    const finalSize: WidthHeightSize = {
        width: targetWidth,
        height: targetHeight,
        size,
    };

    let result: ResolvedImageData;
    if (contentType === 'image/svg+xml') {
        const svgSource = generateImageSource(syntheticAsset, targetWidth, {});
        const svgSources = IMAGE_FORMATS.reduce((acc, format) => {
            acc[format] = [svgSource];
            return acc;
        }, {} as Record<ImageFormat, ImageSource[]>);
        result = generateResolvedImage(syntheticAsset, svgSources, finalSize);
    } else {
        const sources = generateResponsiveImageSources(syntheticAsset, targetWidth, IMAGE_FORMATS);
        result = generateResolvedImage(syntheticAsset, sources, finalSize);
    }

    if (sizesOverride && result.childImageSharp?.images) {
        const sizeProp = sizesOverride;
        const { images } = result.childImageSharp;
        const { sources } = images;
        const { fallback } = images;
        if (sources && fallback) {
            result.childImageSharp = {
                ...result.childImageSharp,
                images: {
                    ...images,
                    sources: sources.map(s => ({ ...s, sizes: sizeProp })),
                    fallback: {
                        src: fallback.src,
                        srcSet: fallback.srcSet,
                        sizes: sizeProp,
                    },
                },
            };
        }
    }

    return result;
};

export default processImageResolution;
